import { TIME_PERIODS_SECONDS } from '@/utils/timeConstants';

// Types for the calculation functions
export type PoolHourData = {
  periodStartUnix: number;
  volumeUSD: string;
  token0Price?: string;
  token1Price?: string;
};

export type PoolDayData = {
  date: number;
  volumeUSD: string;
};

/**
 * Calculates 24-hour USD volume from hourly subgraph data.
 * Separates data into current 24h period and previous 24h period for comparison.
 * @param hourData - Array of hourly pool data from subgraph (expects 48h of data)
 * @param twentyFourHoursAgo - Unix timestamp marking the start of the current 24h period
 * @returns Object with current 24h volume and previous 24h volume in USD
 */
export function calculate24hVolumeUSD(
  hourData: PoolHourData[],
  twentyFourHoursAgo: number,
): { current: number; previous: number } {
  let current = 0;
  let previous = 0;

  for (const hour of hourData) {
    const volumeUSD = parseFloat(hour.volumeUSD) || 0;
    if (hour.periodStartUnix >= twentyFourHoursAgo) {
      current += volumeUSD;
    } else {
      previous += volumeUSD;
    }
  }

  return { current, previous };
}

/**
 * Calculates USD volume from daily subgraph data (fallback when hourly data unavailable).
 * Uses the most recent day as "latest" and second most recent as "previous".
 * @param dayData - Array of daily pool data from subgraph, ordered by date descending
 * @returns Object with latest (today) and previous (yesterday) volume in USD
 */
export function calculateVolumeUSDFromDayData(dayData: PoolDayData[]): {
  latest: number;
  previous: number;
} {
  if (!dayData.length) return { latest: 0, previous: 0 };

  // Get the most recent day's USD volume as "latest" (today)
  const latest = parseFloat(dayData[0]?.volumeUSD ?? '0') || 0;

  // Get the second most recent day's USD volume as "previous" (yesterday)
  const previous = dayData.length > 1 ? parseFloat(dayData[1]?.volumeUSD ?? '0') || 0 : 0;

  return { latest, previous };
}

/**
 * Calculates 24-hour price change from hourly subgraph data.
 * Compares the most recent price to the price closest to 24 hours ago.
 * @param hourData - Array of hourly pool data from subgraph
 * @param priceKey - Which token price to use ('token0Price' or 'token1Price')
 * @param twentyFourHoursAgo - Unix timestamp marking 24 hours ago
 * @returns Object with current price, price 24h ago, and percentage change
 */
export function calculate24hPriceChange(
  hourData: PoolHourData[],
  priceKey: 'token0Price' | 'token1Price',
  twentyFourHoursAgo: number,
): { currentPrice: number; price24hAgo: number; changePercent: number } {
  if (hourData.length === 0) {
    return { currentPrice: 0, price24hAgo: 0, changePercent: 0 };
  }

  // Sort by timestamp ascending to get chronological order
  const sortedData = [...hourData].sort((a, b) => a.periodStartUnix - b.periodStartUnix);

  // Get the most recent price as current
  const currentPrice = parseFloat(sortedData[sortedData.length - 1]?.[priceKey] ?? '0') || 0;

  // Find the price closest to 24h ago
  const dataAround24hAgo = sortedData.filter((h) => h.periodStartUnix <= twentyFourHoursAgo);
  const price24hAgo =
    dataAround24hAgo.length > 0
      ? parseFloat(dataAround24hAgo[dataAround24hAgo.length - 1]?.[priceKey] ?? '0') || 0
      : parseFloat(sortedData[0]?.[priceKey] ?? '0') || 0; // Fallback to oldest available

  // Calculate percentage change
  const changePercent =
    price24hAgo > 0
      ? Math.round(((currentPrice - price24hAgo) / price24hAgo) * 100 * 100) / 100
      : 0;

  return { currentPrice, price24hAgo, changePercent };
}

/**
 * Calculates volume change percentage between two periods.
 * @param currentVolume - Current period volume
 * @param previousVolume - Previous period volume
 * @returns Percentage change rounded to 2 decimal places
 */
export function calculateVolumeChangePercent(
  currentVolume: number,
  previousVolume: number,
): number {
  if (previousVolume <= 0) return 0;
  return Math.round(((currentVolume - previousVolume) / previousVolume) * 100 * 100) / 100;
}

export type TimeRange = '1H' | '1D' | '1W' | '1M' | '1Y' | 'All';

/**
 * Converts a TimeRange selection to API parameters for price history fetching.
 * Determines the appropriate data interval (hourly/daily) and start timestamp.
 * @param timeRange - The selected time range ('1H', '1D', '1W', '1M', '1Y', 'All')
 * @returns Object with interval type and start date as Unix timestamp in seconds
 */
export function getTimeRangeParams(timeRange: TimeRange): { interval: string; startDate: number } {
  const now = Math.floor(Date.now() / 1000);
  switch (timeRange) {
    case '1H':
      return { interval: 'hourly', startDate: now - TIME_PERIODS_SECONDS.ONE_HOUR };
    case '1D':
      return { interval: 'hourly', startDate: now - TIME_PERIODS_SECONDS.TWENTY_FOUR_HOURS };
    case '1W':
      return { interval: 'daily', startDate: now - TIME_PERIODS_SECONDS.SEVEN_DAYS };
    case '1M':
      return { interval: 'daily', startDate: now - TIME_PERIODS_SECONDS.THIRTY_DAYS };
    case '1Y':
      return { interval: 'daily', startDate: now - TIME_PERIODS_SECONDS.ONE_YEAR };
    case 'All':
      return { interval: 'daily', startDate: now - TIME_PERIODS_SECONDS.TWO_YEARS };
  }
}
